import Icon from '@components/Icon'
import {notifIcon, notifTone} from '@constants/notification.constant'
import {NOTIFICATIONS} from '@constants/internal-route.constant'
import {IColors} from '@interface/common.interface'
import {INotification} from '@interface/notification.interface'
import {NotificationService} from '@services/api/notification.service'
import {FC} from 'react'
import {Link, useNavigate} from 'react-router-dom'
import {formatDateTime} from 'utility/formatDate'
import {useT} from 'utility/useT'

type Props = {
  items: INotification[]
  unreadCount: number
  /** Refetch the feed + badge after a read/mark-all so the topbar count stays truthful. */
  onRefresh: () => void
}

/**
 * The topbar bell dropdown. Data comes from sso-service (`public.notifications`) scoped to
 * this app by the token's audience claim, so only HRM notifications appear here.
 *
 * The feed and badge are owned by Topbar (one poll for both); this component only renders
 * and reports interactions back through onRefresh.
 */
const HeaderNotificationsMenu: FC<Props> = ({items, unreadCount, onRefresh}) => {
  const t = useT()
  const navigate = useNavigate()

  const onOpen = (item: INotification) => {
    const go = () => {
      if (item.link) navigate(item.link)
      else navigate(NOTIFICATIONS)
    }

    if (item.isRead) {
      go()
      return
    }

    NotificationService.markRead({id: item.id})
      .catch(() => {})
      .finally(() => {
        onRefresh()
        go()
      })
  }

  const onMarkAllRead = () => {
    NotificationService.markAllRead()
      .catch(() => {})
      .finally(() => onRefresh())
  }

  return (
    <div className='menu menu-sub menu-sub-dropdown menu-column w-350px w-lg-375px' data-kt-menu='true'>
      <div className='d-flex flex-column bgi-no-repeat rounded-top bg-primary'>
        <h3 className='text-white fw-bold px-9 mt-8 mb-6'>
          {t('NOTIFICATION.TITLE')}
          <span className='fs-8 opacity-75 ps-3'>
            {t('NOTIFICATION.UNREAD_COUNT', {count: unreadCount})}
          </span>
        </h3>
      </div>

      <div className='scroll-y mh-325px my-4 px-8'>
        {!items?.length && (
          <div className='text-muted text-center py-8'>{t('NOTIFICATION.EMPTY')}</div>
        )}

        {items?.map((item) => {
          const tone = notifTone(item?.notifType) as IColors
          return (
            <div
              key={item.id}
              className='d-flex flex-stack py-4 cursor-pointer'
              onClick={() => onOpen(item)}
            >
              <div className='d-flex align-items-center'>
                <div className='symbol symbol-35px me-4'>
                  <span className={`symbol-label bg-light-${tone}`}>
                    <Icon icon={notifIcon(item?.notifType)} color={tone} />
                  </span>
                </div>
                <div className='mb-0 me-2'>
                  <div className={`fs-6 text-gray-800 ${item.isRead ? 'fw-semibold opacity-75' : 'fw-bolder'}`}>
                    {item.title}
                  </div>
                  {item.message && <div className='text-gray-400 fs-7'>{item.message}</div>}
                </div>
              </div>
              <span className='badge badge-light fs-8 text-nowrap'>
                {formatDateTime(item.insertedOn)}
              </span>
            </div>
          )
        })}
      </div>

      <div className='py-3 text-center border-top d-flex justify-content-center gap-3'>
        {unreadCount > 0 && (
          <button
            type='button'
            className='btn btn-sm btn-color-gray-600 btn-active-color-primary'
            onClick={onMarkAllRead}
          >
            {t('NOTIFICATION.MARK_ALL_READ')}
          </button>
        )}
        <Link to={NOTIFICATIONS} className='btn btn-sm btn-color-gray-600 btn-active-color-primary'>
          {t('NOTIFICATION.VIEW_ALL')}
        </Link>
      </div>
    </div>
  )
}

export {HeaderNotificationsMenu}
